import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../awsAuth';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useUnreadCounts } from '../../contexts/UnreadCountsContext';
import axios from 'axios';

import './Header.scss';

import logo from './img/logo.png';
import logoWhite from './img/logo-white.png';
import plusLogo from './img/LogoPlus.png';
import businessLogo from './img/LogoBusiness.png';

import searchIcon from './img/search.png';
import searchIconActive from './img/search-active.png';

import messageIcon from './img/message.png';
import messageIconActive from './img/message-active.png';

import notificationIcon from './img/notification.png';
import notificationIconActive from './img/notification-active.png';

import loginIcon from './img/login.png';

const DEFAULT_AVATAR_URL =
  'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

const Header = ({
  isLoggedIn,
  userName,
  setIsLoggedIn,
  userAvatarUrl
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchValue.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const { userId } = useAuth();
  const { plan, isActive, isBusiness } = useSubscription();

  const currentPlan = String(plan || "free").toLowerCase();
  const isPlusPlan = currentPlan === "plus";
  const isBusinessPlan = currentPlan === "business" || isBusiness;

  const [scrollingDown, setScrollingDown] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);

  const lastYRef = useRef(0);
  const tickingRef = useRef(false);
  const mountedRef = useRef(false);

  const SCROLL_THRESHOLD = 100;

  const [avatarUrl, setAvatarUrl] = useState(DEFAULT_AVATAR_URL);
  const {
    messagesUnread: unreadCount,
    notifUnread,
    markNotificationsReadLocally,
  } = useUnreadCounts();

  const isActivePath = useCallback(
    (path) => location.pathname === path,
    [location.pathname]
  );

  const shouldShowGlobal =
  location.pathname === '/' ||
  location.pathname === '/saved';

  useLayoutEffect(() => {
    const y = window.scrollY || 0;
    lastYRef.current = y;
    setIsAtTop(y === 0);
    setScrollingDown(false);
    mountedRef.current = true;
  }, [shouldShowGlobal]);

  const onScroll = useCallback(() => {
    const y = window.scrollY || 0;
    const last = lastYRef.current;

    if (y === 0 && !isAtTop) setIsAtTop(true);
    else if (y !== 0 && isAtTop) setIsAtTop(false);

    const goingDown = y > last && y > SCROLL_THRESHOLD && y > 0;

    if (goingDown !== scrollingDown) {
      setScrollingDown(goingDown);
    }

    lastYRef.current = y;
    tickingRef.current = false;
  }, [isAtTop, scrollingDown]);

  useEffect(() => {
    if (!mountedRef.current) return;

    const handler = () => {
      if (tickingRef.current) return;

      tickingRef.current = true;
      window.requestAnimationFrame(onScroll);
    };

    window.addEventListener('scroll', handler, { passive: true });

    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, [onScroll]);

  useEffect(() => {
    if (userAvatarUrl) {
      setAvatarUrl(userAvatarUrl);
      return;
    }

    try {
      const cachedAvatar = localStorage.getItem('cvanto_avatar');

      if (isLoggedIn && cachedAvatar) {
        setAvatarUrl(cachedAvatar);
        return;
      }
    } catch {}

    setAvatarUrl(DEFAULT_AVATAR_URL);
  }, [isLoggedIn, userAvatarUrl]);

  useEffect(() => {
    const loadAvatar = async () => {
      if (!userId) return;

      try {
        const token = await getAccessToken();

        const res = await axios.get(`/api/account/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const avatar = res.data?.avatar;

        if (avatar) {
          setAvatarUrl(avatar);
          localStorage.setItem('cvanto_avatar', avatar);
        }
      } catch (err) {
        console.debug('Header avatar load error', err);
      }
    };

    if (!userAvatarUrl) {
      loadAvatar();
    }
  }, [userId, userAvatarUrl]);

  useEffect(() => {
    if (location.pathname.startsWith('/notifications')) {
      markNotificationsReadLocally();
    }
  }, [location.pathname, markNotificationsReadLocally]);

  const effectiveUnread = location.pathname.startsWith('/messages') ? 0 : unreadCount;
  const displayUnread = effectiveUnread > 99 ? '99+' : String(effectiveUnread);

  const effectiveNotif = location.pathname.startsWith('/notifications') ? 0 : notifUnread;
  const displayNotif = effectiveNotif > 99 ? '99+' : String(effectiveNotif);

  const handleLogoClick = (e) => {
    e.preventDefault();

    if (location.pathname === '/') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const handleNotificationsClick = async () => {
    await markNotificationsReadLocally();
  };

  const handleCreateClick = () => {
    navigate('/add-ad');
  };

  if (!shouldShowGlobal) return null;

  return (
    <header
      className={`header ${scrollingDown ? 'header--hidden' : ''}`}
      data-premium={isActive ? 'true' : 'false'}
      role="banner"
      aria-hidden={scrollingDown ? 'true' : 'false'}
    >
      <div className="header__mobile-left mobile-only">
        <Link
          to="/notifications"
          className="action-btn action-btn--notifications"
          aria-label="Сповіщення"
          onClick={handleNotificationsClick}
        >
          <img
            src={isActivePath('/notifications') ? notificationIconActive : notificationIcon}
            alt=""
            width={30}
            height={30}
            decoding="async"
          />

          {effectiveNotif > 0 && (
            <span
              className={`cv-badge ${effectiveNotif > 99 ? 'cv-badge--max' : ''}`}
              aria-label={`Непрочитаних сповіщень: ${displayNotif}`}
            >
              {displayNotif}
            </span>
          )}
        </Link>
      </div>

      <div className="header__logo">
        <Link to="/" aria-label="На головну" onClick={handleLogoClick}>
          {!isPlusPlan && !isBusinessPlan && (
            <>
              <img
                src={logo}
                alt="Логотип"
                className="logo logo--light"
                width={140}
                height={35}
                decoding="async"
                loading="eager"
              />

              <img
                src={logoWhite}
                alt="Логотип"
                className="logo logo--dark"
                width={140}
                height={35}
                decoding="async"
                loading="eager"
              />
            </>
          )}

          {isPlusPlan && (
  <img
    src={plusLogo}
    alt="Cvanto Plus"
    className="logo"
    width={140}
    height={35}
    decoding="async"
    loading="eager"
  />
)}

        {isBusinessPlan && (
          <img
            src={businessLogo}
            alt="Cvanto Business"
            className="logo"
            width={140}
            height={35}
            decoding="async"
            loading="eager"
          />
        )}
        </Link>
      </div>

      <div className="header__mobile-right mobile-only">
        <button
          type="button"
          className="action-btn action-btn--create"
          aria-label="Створити"
          onClick={handleCreateClick}
        >
          +
        </button>
      </div>

      <form className="header__search desktop-only" onSubmit={submitSearch} role="search">
        <img src={searchIcon} alt="" width={18} height={18} decoding="async" />
        <input
          type="search"
          className="header__search-input"
          placeholder="Пошук на Cvanto"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          aria-label="Пошук"
        />
      </form>

      <div className="header__actions desktop-only">
        <button
          type="button"
          className="action-btn"
          aria-label="Пошук"
          onClick={submitSearch}
        >
          <img
            src={isActivePath('/search') ? searchIconActive : searchIcon}
            alt=""
            width={30}
            height={30}
            decoding="async"
          />
        </button>

        <Link
          to="/messages"
          className="action-btn action-btn--messages"
          aria-label="Повідомлення"
        >
          <img
            src={isActivePath('/messages') ? messageIconActive : messageIcon}
            alt=""
            width={30}
            height={30}
            decoding="async"
          />

          {effectiveUnread > 0 && (
            <span
              className={`cv-badge ${effectiveUnread > 99 ? 'cv-badge--max' : ''}`}
              aria-label={`Непрочитаних: ${displayUnread}`}
            >
              {displayUnread}
            </span>
          )}
        </Link>

        <div className="action-btn desktop-only">
          {isLoggedIn ? (
            <Link to="/profile" aria-label="Профіль">
              <img
                src={avatarUrl}
                alt=""
                className="profile-avatar"
                width={32}
                height={32}
                decoding="async"
              />
            </Link>
          ) : (
            <Link to="/login" aria-label="Вхід">
              <img src={loginIcon} alt="" width={30} height={30} decoding="async" />
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;